import React, { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceMicButton - Records audio and transcribes/translates to English
 * @param {function} onTranscribed - Callback with the translated English text
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the button
 */
export default function VoiceMicButton({ onTranscribed, className = "", disabled = false, "data-testid": dataTestId = "voice-mic-button" }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format which is well supported
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recorded audio
        const audioBlob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Recording... Speak now");
    } catch (error) {
      console.error("Error starting recording:", error);
      if (error.name === "NotAllowedError") {
        toast.error("Microphone access denied. Please allow microphone access and try again.");
      } else {
        toast.error("Could not start recording. Please check your microphone.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const response = await axios.post(`${API}/voice/transcribe-translate`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        const { translated_text, original_text } = response.data;
        
        if (translated_text) {
          onTranscribed(translated_text);
          
          // Show original if different from translation
          if (original_text && original_text !== translated_text) {
            toast.success(`Translated from: "${original_text.substring(0, 50)}${original_text.length > 50 ? '...' : ''}"`);
          } else {
            toast.success("Voice captured successfully");
          }
        } else {
          toast.warning("No speech detected. Please try again.");
        }
      } else {
        toast.error(response.data.message || "Could not process audio");
      }
    } catch (error) {
      console.error("Error processing audio:", error);
      const errorMsg = error.response?.data?.detail || "Failed to process voice recording";
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Button
      type="button"
      variant={isRecording ? "destructive" : "outline"}
      size="icon"
      onClick={handleClick}
      disabled={disabled || isProcessing}
      className={`shrink-0 ${className}`}
      title={isRecording ? "Stop recording" : "Record voice (speaks in any language, converts to English)"}
      data-testid={dataTestId}
    >
      {isProcessing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isRecording ? (
        <MicOff className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </Button>
  );
}
